import { create } from 'zustand';
import {
  isNewer,
  LIMITS,
  type Channel,
  type Member,
  type Message,
  type MessagePreview,
  type NotificationSetting,
  type PresenceStatus,
  type PublicUser,
  type ReactionSummary,
  type ReadState,
  type ReadyPayload,
  type Relationship,
  type Role,
  type SelfUser,
  type Space,
  type VoiceState,
} from '@spawnwire/shared';

export type ConnectionState = 'connecting' | 'ready' | 'reconnecting' | 'offline';

export interface UiMessage extends Message {
  status?: 'sending' | 'failed';
  error?: string;
}

export interface ChannelMessages {
  /** Ascending ids; optimistic messages use `pending:<nonce>` ids and stay at the end. */
  ids: string[];
  byId: Record<string, UiMessage>;
  loaded: boolean;
  hasMoreBefore: boolean;
  loadingBefore: boolean;
  /** Whether the newest message held is the newest in the channel. */
  atLatest: boolean;
}

export type SpaceMeta = Omit<Space, 'channels'>;

export interface AppState {
  me: SelfUser | null;
  sessionId: string | null;
  connection: ConnectionState;
  spaces: Record<string, SpaceMeta>;
  channels: Record<string, Channel>;
  myMembers: Record<string, Member>;
  members: Record<string, Record<string, Member>>;
  membersLoaded: Record<string, boolean>;
  users: Record<string, PublicUser>;
  presences: Record<string, PresenceStatus>;
  relationships: Record<string, Relationship>;
  readStates: Record<string, ReadState>;
  notificationSettings: Record<string, NotificationSetting>;
  messages: Record<string, ChannelMessages>;
  typing: Record<string, Record<string, number>>;
  voiceStates: Record<string, Record<string, VoiceState>>;
  ringing: { channelId: string; callerId: string } | null;
}

const initial: AppState = {
  me: null,
  sessionId: null,
  connection: 'connecting',
  spaces: {},
  channels: {},
  myMembers: {},
  members: {},
  membersLoaded: {},
  users: {},
  presences: {},
  relationships: {},
  readStates: {},
  notificationSettings: {},
  messages: {},
  typing: {},
  voiceStates: {},
  ringing: null,
};

export const useStore = create<AppState>(() => ({ ...initial }));
const set = useStore.setState;
const get = useStore.getState;

const emptyMessages = (): ChannelMessages => ({ ids: [], byId: {}, loaded: false, hasMoreBefore: true, loadingBefore: false, atLatest: true });

function insertSorted(ids: string[], id: string): string[] {
  if (ids.includes(id)) return ids;
  const real = ids.filter((x) => !x.startsWith('pending:'));
  const pending = ids.filter((x) => x.startsWith('pending:'));
  let lo = 0;
  let hi = real.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (real[mid]! < id) lo = mid + 1;
    else hi = mid;
  }
  real.splice(lo, 0, id);
  return [...real, ...pending];
}

function collectUsers(users: Record<string, PublicUser>, list: (PublicUser | null | undefined)[]): Record<string, PublicUser> {
  let next = users;
  for (const u of list) {
    if (!u) continue;
    if (next === users) next = { ...users };
    next[u.id] = u;
  }
  return next;
}

function usersFromMessage(m: Message): (PublicUser | null)[] {
  return [m.author, m.replyTo?.author ?? null];
}

/** The conversation list's preview of a message. */
function previewOf(m: Message): MessagePreview {
  return { id: m.id, authorId: m.author?.id ?? null, type: m.type, content: m.content.slice(0, LIMITS.messagePreview), attachments: m.attachments.length };
}

const isPrivate = (c: Channel | undefined) => c?.type === 'dm' || c?.type === 'group_dm';

// ---- Session ---------------------------------------------------------------

export const actions = {
  reset() {
    set({ ...initial, connection: 'offline' });
  },

  setMe(me: SelfUser | null) {
    set((s) => ({ me, users: me ? { ...s.users, [me.id]: me } : s.users }));
  },

  setConnection(connection: ConnectionState) {
    set({ connection });
  },

  applyReady(ready: ReadyPayload) {
    const channels: Record<string, Channel> = {};
    const spaces: Record<string, SpaceMeta> = {};
    const users: Record<string, PublicUser> = { ...get().users, [ready.user.id]: ready.user };
    for (const space of ready.spaces) {
      const { channels: list, ...meta } = space;
      spaces[space.id] = meta;
      for (const c of list) channels[c.id] = c;
    }
    // Keep threads the client already knows about; READY lists top-level channels only.
    for (const c of Object.values(get().channels)) if (c.type === 'thread' && c.spaceId && spaces[c.spaceId]) channels[c.id] = c;
    for (const c of ready.privateChannels) {
      channels[c.id] = c;
      for (const r of c.recipients ?? []) users[r.id] = r;
    }
    const myMembers: Record<string, Member> = {};
    for (const m of ready.members) myMembers[m.spaceId] = m;
    const relationships: Record<string, Relationship> = {};
    for (const r of ready.relationships) {
      relationships[r.userId] = r;
      users[r.userId] = r.user;
    }
    const voiceStates: Record<string, Record<string, VoiceState>> = {};
    for (const v of ready.voiceStates) (voiceStates[v.channelId] ??= {})[v.userId] = v;
    set((s) => ({
      me: ready.user,
      sessionId: ready.sessionId,
      spaces,
      channels,
      myMembers,
      users,
      presences: { ...ready.presences, ...(ready.user.status !== 'invisible' ? { [ready.user.id]: s.presences[ready.user.id] ?? 'online' } : {}) },
      relationships,
      readStates: Object.fromEntries(ready.readStates.map((r) => [r.channelId, r])),
      notificationSettings: Object.fromEntries(ready.notificationSettings.map((n) => [n.targetId, n])),
      voiceStates,
      // Member lists are refetched lazily after reconnecting.
      membersLoaded: {},
    }));
  },

  // ---- Spaces and channels ---------------------------------------------------

  upsertSpace(space: Space, member: Member) {
    set((s) => {
      const channels = { ...s.channels };
      for (const [id, c] of Object.entries(channels)) {
        if (c.spaceId === space.id && c.type !== 'thread') delete channels[id];
      }
      for (const c of space.channels) channels[c.id] = c;
      const { channels: _list, ...meta } = space;
      return {
        spaces: { ...s.spaces, [space.id]: meta },
        channels,
        myMembers: { ...s.myMembers, [space.id]: member },
        users: { ...s.users, [member.user.id]: member.user },
      };
    });
  },

  updateSpaceSummary(summary: Omit<Space, 'channels' | 'roles'>) {
    set((s) => {
      const existing = s.spaces[summary.id];
      if (!existing) return {};
      return { spaces: { ...s.spaces, [summary.id]: { ...existing, ...summary } } };
    });
  },

  removeSpace(id: string) {
    set((s) => {
      const spaces = { ...s.spaces };
      delete spaces[id];
      const channels = Object.fromEntries(Object.entries(s.channels).filter(([, c]) => c.spaceId !== id));
      const myMembers = { ...s.myMembers };
      delete myMembers[id];
      return { spaces, channels, myMembers };
    });
  },

  upsertChannel(channel: Channel) {
    set((s) => {
      const users = collectUsers(s.users, channel.recipients ?? []);
      const prev = s.channels[channel.id];
      // Never move lastMessageId (or the preview) backwards because of an out-of-date payload.
      const stale = prev && isNewer(prev.lastMessageId, channel.lastMessageId);
      const lastMessageId = stale ? prev.lastMessageId : channel.lastMessageId;
      const lastMessage = stale || channel.lastMessage === undefined ? prev?.lastMessage : channel.lastMessage;
      return { channels: { ...s.channels, [channel.id]: { ...prev, ...channel, lastMessageId, lastMessage } }, users };
    });
  },

  removeChannel(id: string) {
    set((s) => {
      const channels = { ...s.channels };
      delete channels[id];
      const messages = { ...s.messages };
      delete messages[id];
      return { channels, messages };
    });
  },

  upsertRole(role: Role) {
    set((s) => {
      const space = s.spaces[role.spaceId];
      if (!space) return {};
      const roles = [...space.roles.filter((r) => r.id !== role.id), role].sort((a, b) => a.position - b.position);
      return { spaces: { ...s.spaces, [role.spaceId]: { ...space, roles } } };
    });
  },

  removeRole(spaceId: string, roleId: string) {
    set((s) => {
      const space = s.spaces[spaceId];
      if (!space) return {};
      return { spaces: { ...s.spaces, [spaceId]: { ...space, roles: space.roles.filter((r) => r.id !== roleId) } } };
    });
  },

  // ---- Members and users -----------------------------------------------------

  setMembers(spaceId: string, list: Member[]) {
    set((s) => ({
      members: { ...s.members, [spaceId]: Object.fromEntries(list.map((m) => [m.user.id, m])) },
      membersLoaded: { ...s.membersLoaded, [spaceId]: true },
      users: collectUsers(s.users, list.map((m) => m.user)),
    }));
  },

  upsertMember(member: Member) {
    set((s) => {
      const out: Partial<AppState> = { users: { ...s.users, [member.user.id]: member.user } };
      if (s.members[member.spaceId]) out.members = { ...s.members, [member.spaceId]: { ...s.members[member.spaceId], [member.user.id]: member } };
      if (member.user.id === s.me?.id) out.myMembers = { ...s.myMembers, [member.spaceId]: member };
      return out;
    });
  },

  removeMember(spaceId: string, userId: string) {
    set((s) => {
      if (!s.members[spaceId]) return {};
      const list = { ...s.members[spaceId] };
      delete list[userId];
      const space = s.spaces[spaceId];
      return { members: { ...s.members, [spaceId]: list }, spaces: space ? { ...s.spaces, [spaceId]: { ...space, memberCount: Math.max(0, space.memberCount - 1) } } : s.spaces };
    });
  },

  upsertUser(user: PublicUser) {
    set((s) => {
      const members = { ...s.members };
      for (const [spaceId, list] of Object.entries(members)) {
        if (list[user.id]) members[spaceId] = { ...list, [user.id]: { ...list[user.id]!, user } };
      }
      const relationships = s.relationships[user.id] ? { ...s.relationships, [user.id]: { ...s.relationships[user.id]!, user } } : s.relationships;
      return { users: { ...s.users, [user.id]: user }, members, relationships };
    });
  },

  setPresence(userId: string, status: PresenceStatus) {
    set((s) => ({ presences: { ...s.presences, [userId]: status } }));
  },

  upsertRelationship(r: Relationship) {
    set((s) => ({ relationships: { ...s.relationships, [r.userId]: r }, users: { ...s.users, [r.userId]: r.user } }));
  },

  removeRelationship(userId: string) {
    set((s) => {
      const relationships = { ...s.relationships };
      delete relationships[userId];
      return { relationships };
    });
  },

  setReadState(state: ReadState) {
    set((s) => {
      const prev = s.readStates[state.channelId];
      const lastReadMessageId = prev && isNewer(prev.lastReadMessageId, state.lastReadMessageId) ? prev.lastReadMessageId : state.lastReadMessageId;
      return { readStates: { ...s.readStates, [state.channelId]: { ...state, lastReadMessageId } } };
    });
  },

  setNotificationSetting(setting: NotificationSetting) {
    set((s) => ({ notificationSettings: { ...s.notificationSettings, [setting.targetId]: setting } }));
  },

  // ---- Messages ---------------------------------------------------------------

  setMessagesLoading(channelId: string, loading: boolean) {
    set((s) => ({ messages: { ...s.messages, [channelId]: { ...(s.messages[channelId] ?? emptyMessages()), loadingBefore: loading } } }));
  },

  /** Merge a page of history. `latest` and `around` replace the window; `before`/`after` extend it. */
  mergeMessages(channelId: string, list: Message[], mode: 'latest' | 'before' | 'after' | 'around', pageSize: number) {
    set((s) => {
      const prev = s.messages[channelId] ?? emptyMessages();
      const replace = mode === 'latest' || mode === 'around';
      const byId: Record<string, UiMessage> = replace
        ? Object.fromEntries(Object.entries(prev.byId).filter(([id]) => id.startsWith('pending:')))
        : { ...prev.byId };
      let ids = replace ? prev.ids.filter((id) => id.startsWith('pending:')) : [...prev.ids];
      for (const m of list) {
        byId[m.id] = m;
        ids = insertSorted(ids, m.id);
      }
      const users = collectUsers(s.users, list.flatMap(usersFromMessage));
      return {
        users,
        messages: {
          ...s.messages,
          [channelId]: {
            ids,
            byId,
            loaded: true,
            loadingBefore: false,
            hasMoreBefore: mode === 'after' ? prev.hasMoreBefore : list.length >= (mode === 'around' ? Math.floor(pageSize / 2) : pageSize) || (mode === 'around' && list.length > 0),
            atLatest: mode === 'latest' ? true : mode === 'after' ? list.length < pageSize : mode === 'around' ? false : prev.atLatest,
          },
        },
      };
    });
  },

  upsertMessage(message: Message & { nonce?: string | null }, opts: { fromSocket?: boolean } = {}) {
    set((s) => {
      const channel = s.channels[message.channelId];
      const out: Partial<AppState> = { users: collectUsers(s.users, usersFromMessage(message)) };
      if (channel && isNewer(message.id, channel.lastMessageId)) {
        out.channels = { ...s.channels, [message.channelId]: { ...channel, lastMessageId: message.id, ...(isPrivate(channel) ? { lastMessage: previewOf(message) } : {}) } };
      }
      const list = s.messages[message.channelId];
      if (list && (list.atLatest || list.byId[message.id] || !opts.fromSocket)) {
        const byId = { ...list.byId };
        let ids = list.ids;
        if (message.nonce && byId[`pending:${message.nonce}`]) {
          delete byId[`pending:${message.nonce}`];
          ids = ids.filter((id) => id !== `pending:${message.nonce}`);
        }
        const existing = byId[message.id];
        // Updates broadcast to many people carry no per-user reaction state; keep ours.
        byId[message.id] = existing ? { ...existing, ...message, reactions: existing.reactions, status: undefined } : message;
        ids = insertSorted(ids, message.id);
        out.messages = { ...s.messages, [message.channelId]: { ...list, ids, byId } };
      }
      if (message.author?.id === s.me?.id && message.type !== 'member_join') {
        const prev = s.readStates[message.channelId];
        if (!prev || isNewer(message.id, prev.lastReadMessageId)) {
          out.readStates = { ...s.readStates, [message.channelId]: { channelId: message.channelId, lastReadMessageId: message.id, mentionCount: 0 } };
        }
      }
      // A message from someone clears their typing indicator.
      if (message.author && s.typing[message.channelId]?.[message.author.id]) {
        const t = { ...s.typing[message.channelId] };
        delete t[message.author.id];
        out.typing = { ...s.typing, [message.channelId]: t };
      }
      return out;
    });
  },

  updateMessage(message: Message) {
    set((s) => {
      const channel = s.channels[message.channelId];
      // An edit to a conversation's newest message changes its preview, loaded or not.
      const channels = channel?.lastMessage?.id === message.id ? { ...s.channels, [channel.id]: { ...channel, lastMessage: previewOf(message) } } : s.channels;
      const list = s.messages[message.channelId];
      if (!list?.byId[message.id]) return { channels };
      const existing = list.byId[message.id]!;
      return {
        channels,
        users: collectUsers(s.users, usersFromMessage(message)),
        messages: { ...s.messages, [message.channelId]: { ...list, byId: { ...list.byId, [message.id]: { ...existing, ...message, reactions: existing.reactions } } } },
      };
    });
  },

  removeMessage(channelId: string, id: string) {
    set((s) => {
      const list = s.messages[channelId];
      const channel = s.channels[channelId];
      let channels = s.channels;
      if (channel?.lastMessage?.id === id) {
        // Show the message before it when it is loaded; otherwise no preview until the next one.
        const before = list?.ids.filter((x) => x !== id && !x.startsWith('pending:')).at(-1);
        const prev = before ? list!.byId[before] : undefined;
        channels = { ...s.channels, [channelId]: { ...channel, lastMessageId: prev?.id ?? channel.lastMessageId, lastMessage: prev ? previewOf(prev) : null } };
      }
      if (!list?.byId[id]) return { channels };
      const byId = { ...list.byId };
      delete byId[id];
      // Replies to the deleted message now point at nothing.
      for (const [mid, m] of Object.entries(byId)) {
        if (m.replyTo?.id === id) byId[mid] = { ...m, replyTo: { ...m.replyTo, deleted: true, content: '', author: null } };
      }
      return { channels, messages: { ...s.messages, [channelId]: { ...list, ids: list.ids.filter((x) => x !== id), byId } } };
    });
  },

  addPending(message: UiMessage, nonce: string) {
    set((s) => {
      const list = s.messages[message.channelId] ?? { ...emptyMessages(), loaded: true };
      const id = `pending:${nonce}`;
      return { messages: { ...s.messages, [message.channelId]: { ...list, ids: [...list.ids.filter((x) => x !== id), id], byId: { ...list.byId, [id]: { ...message, id, status: 'sending' } } } } };
    });
  },

  failPending(channelId: string, nonce: string, error: string) {
    set((s) => {
      const list = s.messages[channelId];
      const id = `pending:${nonce}`;
      if (!list?.byId[id]) return {};
      return { messages: { ...s.messages, [channelId]: { ...list, byId: { ...list.byId, [id]: { ...list.byId[id]!, status: 'failed', error } } } } };
    });
  },

  dropPending(channelId: string, nonce: string) {
    set((s) => {
      const list = s.messages[channelId];
      const id = `pending:${nonce}`;
      if (!list?.byId[id]) return {};
      const byId = { ...list.byId };
      delete byId[id];
      return { messages: { ...s.messages, [channelId]: { ...list, ids: list.ids.filter((x) => x !== id), byId } } };
    });
  },

  applyReactions(channelId: string, messageId: string, reactions: Omit<ReactionSummary, 'me'>[], userId: string, emoji: string, added: boolean) {
    set((s) => {
      const list = s.messages[channelId];
      const msg = list?.byId[messageId];
      if (!list || !msg) return {};
      const mine = s.me?.id === userId;
      const next = reactions.map((r) => {
        const prev = msg.reactions.find((p) => p.emoji === r.emoji);
        const me = mine && r.emoji === emoji ? added : (prev?.me ?? false);
        return { ...r, me };
      });
      return { messages: { ...s.messages, [channelId]: { ...list, byId: { ...list.byId, [messageId]: { ...msg, reactions: next } } } } };
    });
  },

  /** Optimistic local reaction toggle; the server event reconciles counts. */
  toggleReactionLocal(channelId: string, messageId: string, emoji: string, add: boolean) {
    set((s) => {
      const list = s.messages[channelId];
      const msg = list?.byId[messageId];
      if (!list || !msg) return {};
      let reactions = msg.reactions.map((r) => (r.emoji === emoji ? { ...r, me: add, count: Math.max(0, r.count + (add ? (r.me ? 0 : 1) : r.me ? -1 : 0)) } : r));
      if (add && !reactions.some((r) => r.emoji === emoji)) reactions = [...reactions, { emoji, count: 1, me: true }];
      reactions = reactions.filter((r) => r.count > 0);
      return { messages: { ...s.messages, [channelId]: { ...list, byId: { ...list.byId, [messageId]: { ...msg, reactions } } } } };
    });
  },

  // ---- Typing and voice -------------------------------------------------------

  setTyping(channelId: string, userId: string, at: number) {
    set((s) => ({ typing: { ...s.typing, [channelId]: { ...(s.typing[channelId] ?? {}), [userId]: at } } }));
  },

  pruneTyping(now: number) {
    const { typing } = get();
    let changed = false;
    const next: AppState['typing'] = {};
    for (const [channelId, users] of Object.entries(typing)) {
      const kept = Object.fromEntries(Object.entries(users).filter(([, at]) => now - at < 9000));
      if (Object.keys(kept).length !== Object.keys(users).length) changed = true;
      if (Object.keys(kept).length) next[channelId] = kept;
    }
    if (changed) set({ typing: next });
  },

  setVoiceState(channelId: string, userId: string, state: VoiceState | null) {
    set((s) => {
      const voiceStates = { ...s.voiceStates };
      // A user is in one call at a time; drop them from any other channel.
      for (const [cid, users] of Object.entries(voiceStates)) {
        if (users[userId] && (cid !== channelId || !state)) {
          const copy = { ...users };
          delete copy[userId];
          voiceStates[cid] = copy;
        }
      }
      if (state) voiceStates[channelId] = { ...(voiceStates[channelId] ?? {}), [userId]: state };
      return { voiceStates };
    });
  },

  setVoiceChannelStates(channelId: string, states: VoiceState[]) {
    set((s) => ({ voiceStates: { ...s.voiceStates, [channelId]: Object.fromEntries(states.map((v) => [v.userId, v])) } }));
  },

  setRinging(ringing: AppState['ringing']) {
    set({ ringing });
  },
};

// ---- Selectors ---------------------------------------------------------------

export function channelsOfSpace(state: Pick<AppState, 'channels'>, spaceId: string): Channel[] {
  return Object.values(state.channels)
    .filter((c) => c.spaceId === spaceId && c.type !== 'thread')
    .sort((a, b) => a.position - b.position || a.createdAt.localeCompare(b.createdAt));
}

export function isUnread(state: Pick<AppState, 'readStates'>, channel: Channel): boolean {
  if (!channel.lastMessageId) return false;
  const rs = state.readStates[channel.id];
  return isNewer(channel.lastMessageId, rs?.lastReadMessageId ?? null);
}

export function mentionCount(state: Pick<AppState, 'readStates'>, channelId: string): number {
  return state.readStates[channelId]?.mentionCount ?? 0;
}

export function isMuted(state: Pick<AppState, 'notificationSettings'>, targetId: string | null | undefined): boolean {
  if (!targetId) return false;
  const s = state.notificationSettings[targetId];
  return !!s?.mutedUntil && new Date(s.mutedUntil).getTime() > Date.now();
}

export function displayNameIn(state: AppState, userId: string, spaceId?: string | null): string {
  const member = spaceId ? (state.members[spaceId]?.[userId] ?? (state.myMembers[spaceId]?.user.id === userId ? state.myMembers[spaceId] : undefined)) : undefined;
  return member?.nickname || state.users[userId]?.displayName || member?.user.displayName || 'Unknown user';
}

/** Colour of a member's highest coloured role. */
export function roleColorOf(state: AppState, spaceId: string | null | undefined, userId: string): string | null {
  if (!spaceId) return null;
  const member = state.members[spaceId]?.[userId] ?? (state.myMembers[spaceId]?.user.id === userId ? state.myMembers[spaceId] : undefined);
  const space = state.spaces[spaceId];
  if (!member || !space) return null;
  const roles = space.roles.filter((r) => member.roleIds.includes(r.id) && r.color).sort((a, b) => b.position - a.position);
  return roles[0]?.color ?? null;
}

export function privateChannelName(state: AppState, channel: Channel): string {
  if (channel.type === 'group_dm') {
    if (channel.name) return channel.name;
    const others = (channel.recipients ?? []).filter((r) => r.id !== state.me?.id);
    return others.map((r) => state.users[r.id]?.displayName ?? r.displayName).join(', ') || 'Empty group';
  }
  const other = (channel.recipients ?? []).find((r) => r.id !== state.me?.id);
  return other ? (state.users[other.id]?.displayName ?? other.displayName) : 'Direct message';
}

export function dmPartner(state: AppState, channel: Channel): PublicUser | null {
  const other = (channel.recipients ?? []).find((r) => r.id !== state.me?.id);
  return other ? (state.users[other.id] ?? other) : null;
}
